// What a caller SENDS to move players in a league (BACKLOG E5): an add or a
// drop, a waiver claim, and the order of my claims.
//
// Views live in `views.ts`; these are requests, the split `league.ts` and
// `auth.ts` make. Served since W7 of PLAN-2026-09-24-free-agents-and-waivers
// (API.md §3), so from here they change additively. THE TEAM IS NOT IN ANY
// BODY: the path names the league and the session names the manager, so a
// body cannot name somebody else's team — `SaveDraftPlanRequest`'s rule.

import { z } from 'zod';

/** A player id as the directory mints it. */
const PlayerId = z.string().min(1).max(64);

/** More than any roster holds, so the cap only ever refuses a malformed body. */
const MAX_IDS = 40;

const noRepeats = (ids: readonly string[]) => new Set(ids).size === ids.length;

/**
 * `POST /leagues/:id/transactions` — add a free agent, drop a player, or both
 * in one move (API.md §4.3: `{ add?, drop?[] }`).
 *
 * ONE MOVE, ONE TRANSACTION. An add that needs room names its drop in the same
 * body, so the league never sees a roster that was over its limit in between,
 * and an undo takes back both halves in one step (WAIVERS.md "What the build
 * settled"). `drop` is a list because a roster rule can need two out for one
 * in; an empty list is the plain add.
 */
export const AddDropRequest = z
  .object({
    /** The free agent to add. Absent for a plain drop. */
    add: PlayerId.optional(),
    /** My players to drop. Absent or empty for a plain add. */
    drop: z
      .array(PlayerId)
      .max(MAX_IDS)
      .refine(noRepeats, 'Each player can be dropped once.')
      .default([]),
  })
  .superRefine((body, ctx) => {
    if (body.add === undefined && body.drop.length === 0)
      ctx.addIssue({
        code: 'custom',
        path: ['add'],
        message: 'Name a player to add, one to drop, or both.',
      });
    if (body.add !== undefined && body.drop.includes(body.add))
      ctx.addIssue({
        code: 'custom',
        path: ['drop'],
        message: 'A player cannot be added and dropped in the same move.',
      });
  });
export type AddDropRequest = z.infer<typeof AddDropRequest>;

/**
 * `POST /leagues/:id/waivers/claims` places a claim;
 * `PUT /leagues/:id/waivers/claims/:claimId` edits one of mine in place, and
 * then `playerId` must be the claim's own player.
 *
 * EVERY FIELD BUT `order` IS STATED, even when it is empty. An edit is the
 * whole claim as it should now stand, so "no drop" and "leave my drop alone"
 * cannot be the same body — the rule `SaveLineupRequest` keeps for a slot.
 */
export const ClaimRequest = z
  .object({
    playerId: PlayerId,
    /** Whole dollars, in a budget league; null in any other. */
    bid: z.number().int().min(0).nullable(),
    /** My players to drop if the claim wins. Empty when the roster has room. */
    dropPlayerIds: z
      .array(PlayerId)
      .max(MAX_IDS)
      .refine(noRepeats, 'Each player can be dropped once.'),
    /** "Only if my claim for X fails": one of my own pending claims, or null. */
    conditionalOnClaimId: z.string().min(1).max(128).nullable(),
    /** Where among my pending claims this one is tried, 1 first. Absent: last for a new claim, where it was for an edit. */
    order: z.number().int().min(1).optional(),
  })
  .superRefine((body, ctx) => {
    if (body.dropPlayerIds.includes(body.playerId))
      ctx.addIssue({
        code: 'custom',
        path: ['dropPlayerIds'],
        message: 'A claim cannot drop the player it claims.',
      });
  });
export type ClaimRequest = z.infer<typeof ClaimRequest>;

/**
 * `PUT /leagues/:id/waivers/claims/order` — my pending claims in the order to
 * try them, first first. Every pending claim of mine, each once; a list that
 * leaves one out or names one that is not my pending claim is refused rather
 * than half applied.
 */
export const ClaimOrderRequest = z.object({
  claimIds: z
    .array(z.string().min(1).max(128))
    .min(1)
    .max(MAX_IDS)
    .refine(noRepeats, 'Each claim can be named once.'),
});
export type ClaimOrderRequest = z.infer<typeof ClaimOrderRequest>;

/**
 * `GET /leagues/:id/players/:playerId/move?kind=&claimId=` — which sheet.
 *
 * `kind` IS REQUIRED: the add, claim and drop sheets ask different questions
 * (a claim is judged at its run, an add now), so a default would answer a
 * question the caller may not have asked. `claimId` names one of my pending
 * claims to edit, and only means anything with `kind=claim`.
 */
export const RosterMoveQuery = z
  .object({
    kind: z.enum(['add', 'claim', 'drop']),
    claimId: z.string().min(1).max(128).nullable().default(null),
  })
  .superRefine((q, ctx) => {
    if (q.claimId !== null && q.kind !== 'claim')
      ctx.addIssue({
        code: 'custom',
        path: ['claimId'],
        message: 'A claim to edit goes with kind=claim.',
      });
  });
export type RosterMoveQuery = z.infer<typeof RosterMoveQuery>;

/**
 * `POST /leagues/:id/transactions/:txId/reverse` — a commissioner taking back
 * a processed add, drop, claim or trade (J2, COMMISSIONER_OVERRIDES.md
 * "Reversing a move"). The reason is required and goes on the log, the chat
 * and the notice, as an override's does.
 */
export const ReverseRequest = z.object({
  reason: z.string().trim().min(1, 'Say why.').max(300),
});
export type ReverseRequest = z.infer<typeof ReverseRequest>;

/**
 * `PUT /leagues/:id/teams/:teamId/waiver-priority` — a commissioner moving a
 * team to a place in a stored (rolling) waiver order, 1 being first (J7,
 * COMMISSIONER_OVERRIDES.md). The teams between shift one place to make room.
 */
export const WaiverPriorityRequest = z.object({
  rank: z.number().int().min(1),
  reason: z.string().trim().min(1, 'Say why.').max(300),
});
export type WaiverPriorityRequest = z.infer<typeof WaiverPriorityRequest>;

/**
 * `PUT /leagues/:id/teams/:teamId/budget` — a commissioner setting a team's
 * budget left, in whole dollars from $0 to the league's budget (J7).
 */
export const BudgetRequest = z.object({
  left: z.number().int().min(0),
  reason: z.string().trim().min(1, 'Say why.').max(300),
});
export type BudgetRequest = z.infer<typeof BudgetRequest>;
